/**
 * Entity drive resource (CT-5717): the record's OWN drive on Knowledge — its
 * folders and files, whether Concierge reads them for claims, and creating a
 * folder. NOT `drives.js`, which is the vault picker for note attachments
 * (CT-5528) with the shape `DrivePickerModal` needs.
 *
 * The paths are ASSUMED, `TODO(CT-5717 / backend)`. Real signatures, snake_case
 * bodies; only the transport is stubbed. Search, the chips, the filters and the
 * order are applied on the server.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockEntityDriveForTests, __setMockKnowledgeScenario } from './__mocks__/entityDrive.mock.js';

/** One folder or file row. Optional parts are `null`, never `''`. */
export const mapApiDriveItemToUi = (raw = {}) => ({
  id: raw.id ?? raw.item_id ?? '',
  kind: raw.kind ?? 'file',
  name: raw.name ?? '',
  // pdf | doc | sheet | txt — it picks the row's icon.
  fileType: raw.type ?? raw.file_type ?? raw.fileType ?? null,
  createdBy: raw.created_by ?? raw.createdBy ?? null,
  sourceLine: raw.source_line ?? raw.sourceLine ?? null,
  sizeBytes: raw.size_bytes ?? raw.sizeBytes ?? null,
  occurredAt: raw.occurred_at ?? raw.occurredAt ?? null,
  status: raw.status ?? [],
  fileCount: raw.file_count ?? raw.fileCount ?? null,
  claimsExtracted: raw.claims_extracted ?? raw.claimsExtracted ?? 0,
  read: Boolean(raw.read ?? false),
});

const mapExtraction = (raw) =>
  raw
    ? {
        enabled: Boolean(raw.enabled),
        inherited: Boolean(raw.inherited),
        filesRead: raw.files_read ?? raw.filesRead ?? 0,
        filesTotal: raw.files_total ?? raw.filesTotal ?? 0,
        claimsTotal: raw.claims_total ?? raw.claimsTotal ?? 0,
      }
    : null;

/**
 * One folder of the drive as the page shows it: the drive's name, the folder in
 * view, its path, the tree, its claim extraction, the rows, the "Created by"
 * options and whether the viewer may manage it. Safe defaults for a missing part.
 */
export const mapApiEntityDriveToUi = (raw = {}) => ({
  driveName: raw.drive?.name ?? '',
  folder: raw.folder
    ? { id: raw.folder.id, name: raw.folder.name, sourceLine: raw.folder.source_line ?? raw.folder.sourceLine ?? null }
    : null,
  breadcrumb: (raw.breadcrumb ?? []).map((crumb) => ({ id: crumb.id, name: crumb.name })),
  tree: (raw.tree ?? []).map((node) => ({
    id: node.id,
    name: node.name,
    fileCount: node.file_count ?? node.fileCount ?? 0,
    depth: node.depth ?? 0,
  })),
  extraction: mapExtraction(raw.extraction),
  items: (raw.items ?? []).map(mapApiDriveItemToUi),
  createdByOptions: raw.created_by_options ?? raw.createdByOptions ?? [],
  canManage: Boolean(raw.can_manage ?? raw.canManage ?? false),
});

/**
 * TODO(CT-5717 / backend): `POST /v0/profile/entity-drive/list` with `{ entity_type,
 * entity_id, folder_id, page, page_size, order_by, order_direction, quick_filter,
 * filters: { scope, type, created_by }, search: { query, fields } }`.
 */
export const listEntityDrive = async ({
  entityType,
  entityId,
  folderId = 'root',
  page = 1,
  pageSize = 10,
  orderBy = 'occurred_at',
  orderDirection = 'desc',
  quickFilter = 'all',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/entity-drive/list', {
    entity_type: entityType,
    entity_id: entityId,
    folder_id: folderId,
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    quick_filter: quickFilter,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load the drive');
};

/** TODO(CT-5717 / backend): `POST /v0/profile/entity-drive/extraction/set` with `{ entity_type, entity_id, folder_id, enabled }`. */
export const setDriveExtraction = async ({ entityType, entityId, folderId = 'root', enabled } = {}) => {
  const response = await request('/v0/profile/entity-drive/extraction/set', {
    entity_type: entityType,
    entity_id: entityId,
    folder_id: folderId,
    enabled,
  });
  return validateApiResponse(response, 'Failed to change claim extraction');
};

/**
 * TODO(CT-5717 / backend): `POST /v0/profile/entity-drive/folders/create` with
 * `{ entity_type, entity_id, parent_id, name, description, extract_claims }`.
 * 422 without a name, 409 when the name is taken in that folder.
 */
export const createDriveFolder = async ({ entityType, entityId, parentId = 'root', name, description, extractClaims = true } = {}) => {
  const response = await request('/v0/profile/entity-drive/folders/create', {
    entity_type: entityType,
    entity_id: entityId,
    parent_id: parentId,
    name,
    description,
    extract_claims: extractClaims,
  });
  return validateApiResponse(response, 'Failed to create the folder');
};
